/**
 * Booking Funnel Component
 * Enhanced visual funnel with drop-off indicators
 */

import { ArrowDown, TrendingDown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { BookingFunnelData, FunnelStep } from '@/lib/analytics'

interface BookingFunnelProps {
  data: BookingFunnelData | null | undefined
  isLoading: boolean
}

const STEP_COLORS = [
  'bg-primary',
  'bg-primary/80',
  'bg-primary/60',
  'bg-primary/40',
  'bg-primary/30'
]

function FunnelStepRow({ step, maxCount, index, isLast }: {
  step: FunnelStep
  maxCount: number
  index: number
  isLast: boolean
}) {
  const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{step.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">
            {step.count.toLocaleString()}
          </span>
          {index > 0 && (
            <span className={`text-xs ${step.conversionRate < 10 ? 'text-amber-600' : 'text-muted-foreground'}`}>
              ({step.conversionRate.toFixed(1)}%)
            </span>
          )}
        </div>
      </div>

      <div className="relative h-8 w-full bg-muted rounded overflow-hidden">
        <div
          className={`h-full ${STEP_COLORS[index] || STEP_COLORS[4]} transition-all duration-500 rounded`}
          style={{ width: `${percentage}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          {percentage > 20 && (
            <span className="text-xs font-medium text-white">
              {percentage.toFixed(0)}%
            </span>
          )}
        </div>
      </div>

      {/* Drop-off indicator */}
      {!isLast && step.dropoffRate !== undefined && step.dropoffRate > 0 && (
        <div className="flex items-center justify-center text-xs text-muted-foreground py-1">
          <ArrowDown className="h-3 w-3 mr-1" />
          <span className={step.dropoffRate > 50 ? 'text-red-500' : ''}>
            {step.dropoffRate.toFixed(1)}% drop-off
          </span>
        </div>
      )}
    </div>
  )
}

export function BookingFunnel({ data, isLoading }: BookingFunnelProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Booking Funnel</CardTitle>
          <CardDescription>Track visitors through the booking process</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-8 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.steps.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Booking Funnel</CardTitle>
          <CardDescription>Track visitors through the booking process</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[250px] items-center justify-center text-muted-foreground">
            No booking data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const maxCount = data.steps[0]?.count || 1

  return (
    <Card>
      <CardHeader>
        <CardTitle>Booking Funnel</CardTitle>
        <CardDescription>
          Track visitors through the booking process
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {data.steps.map((step, index) => (
            <FunnelStepRow
              key={step.event}
              step={step}
              maxCount={maxCount}
              index={index}
              isLast={index === data.steps.length - 1}
            />
          ))}
        </div>

        {/* Overall conversion rate */}
        <div className="mt-6 pt-4 border-t flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Overall Conversion</p>
            <p className="text-xs text-muted-foreground">Visitors to completed bookings</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">
              {data.overallConversionRate.toFixed(2)}%
            </p>
            {data.overallConversionRate < 2 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <TrendingDown className="h-3 w-3" />
                Below average
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
